'use strict';

const CriticalNotificationBuilder = require('components/user_area/notifications_manager/js/builders/CriticalNotificationBuilder');
const InformativeNotificationBuilder = require('components/user_area/notifications_manager/js/builders/InformativeNotificationBuilder');
const SuccessfulNotificationBuilder = require('components/user_area/notifications_manager/js/builders/SuccessfulNotificationBuilder');
const {
    BIM_STATUS,
    NOTIFICATIONS_CONTENT,
    REFUND_STEPS,
} = require('components/user_area/incidents_manager/js/utils/constants');

const getNotificationKeys = (widgetKeys, cancellationData, bookingInfo) => {
    const {status} = cancellationData;
    const suffixAlternatives = getSuffixAlternativeStatus(status);
    return {
        headerTitle: getNotificationTitle(widgetKeys, cancellationData, bookingInfo, suffixAlternatives),
        redBoxText: getTitleWithItineraryVariations(widgetKeys, cancellationData, bookingInfo),
        bodyText: getNotificationBodyText(widgetKeys, cancellationData, suffixAlternatives),
        ctaText: widgetKeys[`${cancellationData.status}${suffixAlternatives}CtaTxt`],
        statusLabel: widgetKeys.cancelNotificationStep,
    };
};

const getSuffixAlternativeStatus = (status) => Odigeo.AB.isAlternativeStatus && [BIM_STATUS.REFUND_WAITING_CONSENT,
    BIM_STATUS.RESOLVED_BY_CUSTOMER,
    BIM_STATUS.ALL_REFUND_WAITING_CONSENT,
    BIM_STATUS.ALL_RESOLVED_BY_CUSTOMER]
    .includes(status)
    ? '_STATUS'
    : '';

const getNotificationTitle = (widgetKeys, cancellationData, bookingInfo, suffixAlternatives) => {
    const {status} = cancellationData;
    const defaultTitle = widgetKeys[`${status}${suffixAlternatives}Title`];
    if (NOTIFICATIONS_CONTENT.TITLE_WITH_ITINERARY_VARIATION.includes(status)) {
        return getTitleWithItineraryVariations(widgetKeys, cancellationData, bookingInfo);
    } else if (NOTIFICATIONS_CONTENT.TITLE_WITH_PNRS_HANDLED.includes(status)) {
        return getTitleWithNumberOfPNRsHandled(defaultTitle, cancellationData);
    } else {
        return defaultTitle;
    }
};

const getTitleWithItineraryVariations = (widgetKeys, {pnrsIncluded, status}, bookingInfo) => {
    const sections = getCancelledSections(pnrsIncluded);
    const singularOrPlural = sections.length > 1 ? 'PLURAL' : 'SINGULAR';
    const someOrAll = bookingInfo.areAllItinerarySectionsCancelled ? 'ALLCANCELLED' : 'SOMECANCELLED';
    const keySuffix = `Title::${singularOrPlural}_${someOrAll}`;
    const titleKey = widgetKeys[`${status}${keySuffix}`] || widgetKeys[`CANCELLED${keySuffix}`];
    return Odigeo.Utils.replaceCmsKeyArgs(titleKey, sections[0].section.from.cityName, sections[0].section.to.cityName);
};

const getCancelledSections = (pnrsIncluded) => {
    const allCancelledSections = Object.values(pnrsIncluded).map(({sectionsCancelled}) => sectionsCancelled).flat();
    return allCancelledSections.length ? allCancelledSections : Object.values(pnrsIncluded).map(({sections}) => sections).flat();
};

const getTitleWithNumberOfPNRsHandled = (titleKey, {pnrsIncluded}) => Odigeo.Utils.replaceCmsKeyArgs(titleKey, Object.keys(pnrsIncluded).length);

const getNotificationBodyText = (widgetKeys, {status, pnrsIncluded}, suffixAlternatives) => {
    if (NOTIFICATIONS_CONTENT.BODY_WITH_MERCHANT_VARIATION.includes(status)) {
        return getBodyWithMerchantVariation(widgetKeys, status, Object.values(pnrsIncluded)[0]);
    } else {
        return status === BIM_STATUS.REFUND_MERCHANT || status === BIM_STATUS.REFUND_NO_MERCHANT ?
            Odigeo.Utils.replaceCmsKeyArgs(widgetKeys[`${status}HelperTxt`], '<span class="odf-bold">', '</span>') :
            widgetKeys[`${status}${suffixAlternatives}HelperTxt`];
    }
};

const getBodyWithMerchantVariation = (widgetKeys, status, {merchant}) => {
    const merchantModifier = merchant ? 'Merchant' : 'NonMerchant';
    return Odigeo.Utils.replaceCmsKeyArgs(widgetKeys[`${status}${merchantModifier}HelperTxt`], '<span class="odf-bold">', '</span>');
};

const getStatusInfoBasedOnRefundStatus = (widgetKeys, {pnrsIncluded}) => {
    const firstPNRInfo = Object.values(pnrsIncluded)[0];
    const {refundStatus = '', refund = {}} = firstPNRInfo;
    const firstRefundStatus = refundStatus || refund.status;
    let status;
    if (firstRefundStatus) {
        status = {
            description: widgetKeys[`cancelNotificationStatus::${firstRefundStatus}`],
            currentStep: REFUND_STEPS.indexOf(firstRefundStatus) + 1,
            totalSteps: REFUND_STEPS.length,
        };
    }
    return status;
};

const getNotificationData = (widgetKeys, cancellationData, bookingInfo) => {
    const notificationKeys = getNotificationKeys(widgetKeys, cancellationData, bookingInfo);
    const {status, ctaHandler, extraHeader} = cancellationData;
    let notification;
    let notificationStatus;

    switch (status) {
        case BIM_STATUS.OPEN_TICKET_ACCEPTED:
        case BIM_STATUS.ALL_OPEN_TICKET_ACCEPTED:
            notification = new SuccessfulNotificationBuilder(status, notificationKeys)
                .withOptions({
                    headerIconClass: 'odf-icon-plane-flexibility',
                });
            break;
        case BIM_STATUS.CUSTOMER_ALTERNATIVE_ISSUED:
            notification = new SuccessfulNotificationBuilder(status, notificationKeys)
                .withOptions({
                    headerIconClass: 'odf-icon-tick-circle',
                    hasCTA: false,
                });
            break;
        case BIM_STATUS.OPEN_TICKET_PENDING:
        case BIM_STATUS.ALL_OPEN_TICKET_PENDING:
        case BIM_STATUS.MMB:
        case BIM_STATUS.CONTACT_AIRLINE:
        case BIM_STATUS.ALL_CONTACT_AIRLINE:
        case BIM_STATUS.REFUND_WAITING_CONSENT:
        case BIM_STATUS.ALL_REFUND_WAITING_CONSENT:
        case BIM_STATUS.ALL_BANKRUPTCY:
        case BIM_STATUS.ALL_REFUND_NOT_MANAGED_BY_EDO:
        case BIM_STATUS.ALL_MANAGED_BY_ICM:
        case BIM_STATUS.ALL_MANUAL_AIRLINE_ALTERNATIVE:
        case BIM_STATUS.ALL_CRITICAL:
            notification = new CriticalNotificationBuilder(status, notificationKeys)
                .withOptions({
                    hasCTA: true,
                });
            break;
        case BIM_STATUS.BANKRUPTCY:
        case BIM_STATUS.MANAGED_BY_ICM:
        case BIM_STATUS.MANUAL_AIRLINE_ALTERNATIVE:
            notification = new CriticalNotificationBuilder(status, notificationKeys)
                .withOptions({
                    hasCTA: false,
                });
            break;
        case BIM_STATUS.REFUND_NOT_MANAGED_BY_EDO:
            notification = new CriticalNotificationBuilder(status, notificationKeys)
                .withOptions({
                    hasCTA: true,
                    buttonFullWidth: true,
                });
            break;
        case BIM_STATUS.ALL_RESOLVED_BY_CUSTOMER:
        case BIM_STATUS.RESOLVED_BY_CUSTOMER:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    headerIconClass: Odigeo.AB.isAlternativeStatus ? 'odf-icon-tick-circle' : 'odf-icon-exclamation-circle',
                    contentType: Odigeo.AB.isAlternativeStatus ? 'text' : 'none',
                    hasCTA: false,
                });
            break;
        case BIM_STATUS.CUSTOMER_ALTERNATIVE_REQUESTED:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    contentType: 'text',
                    hasCTA: false,
                });
            break;
        case BIM_STATUS.RESOLVED:
        case BIM_STATUS.ALL_RESOLVED:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    headerIconClass: 'odf-icon-exclamation-circle',
                    contentType: 'text',
                    hasCTA: widgetKeys.isResolvedSurveyEnabled === 'true',
                });
            break;
        case BIM_STATUS.OPEN_TICKET_REJECTED:
        case BIM_STATUS.REFUND:
            notificationStatus = getStatusInfoBasedOnRefundStatus(widgetKeys, cancellationData);
            notification = new InformativeNotificationBuilder(status, notificationKeys).withOptions({
                hasRedBox: extraHeader,
            });
            if (notificationStatus) {
                notification.withStatus(notificationStatus);
            } else {
                notification.withOptions({
                    contentType: 'text',
                    hasCTA: false,
                });
            }
            break;
        case BIM_STATUS.REFUND_WITH_STATUS:
        case BIM_STATUS.REFUND_VOLUNTARY:
            notificationStatus = getStatusInfoBasedOnRefundStatus(widgetKeys, cancellationData);
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    hasRedBox: extraHeader,
                })
                .withStatus(notificationStatus);
            break;
        case BIM_STATUS.REFUND_MERCHANT:
        case BIM_STATUS.REFUND_NO_MERCHANT:
            notification = new InformativeNotificationBuilder(status, notificationKeys).withOptions({
                hasRedBox: extraHeader,
                contentType: 'text',
                hasCTA: false,
            });
            break;
        case BIM_STATUS.REDEMPTION_PENDING:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withStatus({
                    description: widgetKeys['REDEMPTION_PENDINGStep2'],
                    currentStep: 2,
                    totalSteps: 3,
                });
            break;
        case BIM_STATUS.PENDING_CLASSIFICATION:
        case BIM_STATUS.MANAGED_BY_TOUCHLESS:
        case BIM_STATUS.ALL_MANAGED_BY_TOUCHLESS:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    contentType: 'text',
                    hasCTA: false,
                    hasRedBox: extraHeader,
                });
            break;
        case BIM_STATUS.ALL_INFORMATIVE:
        case BIM_STATUS.ALL_REFUND:
        case BIM_STATUS.ALL_REDEMPTION_PENDING:
        case BIM_STATUS.ALL_PENDING_CLASSIFICATION:
            notification = new InformativeNotificationBuilder(status, notificationKeys)
                .withOptions({
                    contentType: 'text',
                    hasCTA: true,
                    hasRedBox: extraHeader,
                });
            break;
        default:
            break;
    }

    if (Odigeo._.isFunction(ctaHandler)) {
        notification.withCTAHandler(ctaHandler);
    }
    return notification;
};

module.exports = {
    getNotificationData,
};
